import React from "react";
import type { Theme } from "../lib/theme";
import type { EscalationSettings, EscalationResult } from "../lib/escalation";
import { fmtMonths } from "../lib/escalation";

/**
 * Escalation, as two numbers.
 *
 * The rate is typed. The months are read from the schedule and shown, so the
 * figure follows the job when the job moves -- which is the whole reason to
 * price escalation here rather than inside a line item nobody can audit.
 *
 * The months stay editable because a schedule is not always loaded and an
 * estimator sometimes knows the midpoint before the bars exist. Editing them
 * is visibly an override, with one click back.
 */
export default function EscalationRow({
  T, FONT, fmt, isLocked, settings, setSettings, result,
}: {
  T: Theme;
  FONT: string;
  fmt: (n: number) => string;
  isLocked: boolean;
  settings: EscalationSettings;
  setSettings: (s: EscalationSettings) => void;
  result: EscalationResult;
}) {
  const priced = settings.annualPct > 0;
  const set = (patch: Partial<EscalationSettings>) => { if (!isLocked) setSettings({ ...settings, ...patch }); };

  // The month the average dollar gets spent, said as a date because "18 mo"
  // is hard to sanity-check and "Jul 2027" is not.
  const midpoint = new Date();
  midpoint.setMonth(midpoint.getMonth() + Math.round(result.months));
  const midpointLabel = midpoint.toLocaleDateString(undefined, { month: "short", year: "numeric" });

  const label: React.CSSProperties = {
    fontSize: 10, color: T.textMuted, letterSpacing: ".12em",
    textTransform: "uppercase", marginBottom: 6,
  };

  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${T.borderPrimary}` }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ ...label, marginBottom: 0 }}>Escalation</div>
        <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums",
          color: priced ? T.negative : T.textWeak }}>
          {priced ? `+${fmt(result.amount)}` : "Not priced"}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, alignItems: "end" }}>
        <div>
          <div style={label}>% per year</div>
          <input className="input-field" type="number" step=".25" min="0"
            value={settings.annualPct}
            onChange={e => set({ annualPct: +e.target.value })}
            readOnly={isLocked}
            style={{ width: "100%", boxSizing: "border-box" }} />
        </div>
        <div>
          <div style={{ ...label, display: "flex", alignItems: "center", gap: 6 }}>
            <span>Months to midpoint</span>
            {!result.derived && !isLocked && (
              <button onClick={() => set({ monthsOverride: null })} title="Go back to reading this from the schedule"
                style={{ fontSize: 10, background: "none", border: "none", padding: 0, cursor: "pointer",
                  color: T.borderAccent, fontFamily: FONT, letterSpacing: 0, textTransform: "none" }}>
                reset
              </button>
            )}
          </div>
          <input className="input-field" type="number" step="1" min="0"
            value={result.derived ? Math.round(result.months * 10) / 10 : (settings.monthsOverride ?? 0)}
            onChange={e => set({ monthsOverride: +e.target.value })}
            readOnly={isLocked}
            style={{ width: "100%", boxSizing: "border-box",
              borderColor: result.derived ? undefined : `${T.borderAccent}88` }} />
        </div>
        <div>
          <div style={label}>Compounded</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: priced ? T.textSecondary : T.textWeak,
            fontVariantNumeric: "tabular-nums", paddingBottom: 6 }}>
            {result.pct.toFixed(2)}%
          </div>
        </div>
      </div>

      <div style={{ fontSize: 11, color: T.textWeak, marginTop: 10, lineHeight: 1.6 }}>
        {priced ? (
          <>
            {result.derived
              ? <>Priced to <strong style={{ color: T.textMuted }}>{midpointLabel}</strong> — the midpoint of construction, {fmtMonths(result.months)} out, read from the project start date and duration.</>
              : <>Priced to <strong style={{ color: T.textMuted }}>{midpointLabel}</strong>, {fmtMonths(result.months)} out. You set this midpoint; it no longer follows the schedule.</>}
            {" "}Half the work is bought before that date and half after, so it is where the average dollar is spent.
            Escalation is a cost of work: overhead and profit are charged on it, and a contingency reserve based on
            direct cost includes it.
          </>
        ) : (
          <>Work bought later costs more. Escalation is a known cost with an unknown amount — price it here rather
          than in contingency, which is for unknown scope and gets spent before the first unknown arrives.</>
        )}
      </div>
    </div>
  );
}
